import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { FormValues } from './CrmField'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetDescribeQuery } from '../../../store/integrations/integrations'
import classNames from 'classnames'

type MissingRequiredFieldsProps = {
  connector: string
}

export const MissingRequiredFields: React.FC<MissingRequiredFieldsProps> = ({ connector }) => {
  const { watch } = useFormContext<FormValues>()
  const formFields = watch('mappingFormFields')
  const selectedObjects = formFields
    .map((field) => field.objectName)
    .filter((value, index, array) => array.indexOf(value) === index)
    .filter((value): value is string => value !== null)

  const [requiredFieldsCount, setRequiredFieldsCount] = useState<{ [key: string]: number }>({})
  const allRequiredFieldsCount = Object.entries(requiredFieldsCount).reduce((acc, [_, count]) => {
    return acc + count
  }, 0)

  const handleRequiredFieldsChange = (object: string, count: number) => {
    setRequiredFieldsCount((prev) => ({ ...prev, ...{ object: count } }))
  }

  return (
    <div
      className={classNames('alert alert-danger mb-3', {
        'd-none': !allRequiredFieldsCount,
      })}
    >
      <strong>Some required CRM fields are missing:</strong>
      {selectedObjects.map((object) => {
        return <MissingObjectRequiredField key={object} connector={connector} object={object} onRequiredFieldsChange={handleRequiredFieldsChange} />
      })}
    </div>
  )
}

type MissingObjectRequiredFieldsProps = {
  connector: string
  object: string
  onRequiredFieldsChange: (object: string, count: number) => void
}

const MissingObjectRequiredField: React.FC<MissingObjectRequiredFieldsProps> = ({ connector, object, onRequiredFieldsChange }) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: objectFields, isFetching: isFieldsLoading } = useGetDescribeQuery({
    accountId,
    connector: connector,
    context: { objectName: object },
  })
  const { watch } = useFormContext<FormValues>()

  const formFields = watch('mappingFormFields')
  const selectedObjectFields = useMemo(() => formFields.filter((field) => field.objectName === object).map((field) => field.fieldName), [formFields])

  const requiredFields = useMemo(
    () =>
      objectFields?.data.describe.formFieldDefinitions
        .filter((field: any) => field.required)
        .filter((field: any) => !selectedObjectFields.includes(field.name)),
    [objectFields, selectedObjectFields],
  )

  useEffect(() => {
    if (!requiredFields) {
      return
    }

    onRequiredFieldsChange(object, requiredFields.length)
  }, [requiredFields])

  if (!objectFields) {
    return null
  }

  if (!requiredFields.length) {
    return <></>
  }

  return (
    <div key={object} className='mt-2'>
      <div>
        <strong>{object}</strong>
      </div>
      <ul className='mb-0'>
        {requiredFields.map(({ name, label }: { name: string; label: string }) => (
          <li key={name}>
            {label} ({name})
          </li>
        ))}
      </ul>
    </div>
  )
}
